import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "components/app/dialog/dialog";
import { CarouselApi } from "components/ui/carousel";
import { Download, Trash2, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "components/ui/alert-dialog";

import {
  Carousel,
  CarouselContent,
  CarouselItem,
} from "components/ui/carousel";
import ImageUrl from "components/app/image-url/ImageUrl";

interface DialogProps {
  onValueDelete: (value: any) => void;
  isDialogOpen: boolean;
  setIsDialogOpen: (value: boolean) => void;
  userAllCoverProfiles: any;
  itemCoverProfile: any;
  // handleDeleteCover: (cover: any) => void;
}

export default function DialogClickCoverProfile({
  userAllCoverProfiles,
  itemCoverProfile,
  onValueDelete,
  isDialogOpen,
  setIsDialogOpen,
}: // handleDeleteCover,
DialogProps) {
  const [api, setApi] = useState<CarouselApi>();
  const [selectedItem, setSelectedItem] = useState<any>(itemCoverProfile);
  const isScrolling = useRef(false);

  // Sync `itemCoverProfile` to `selectedItem`
  useEffect(() => {
    setSelectedItem(itemCoverProfile);
  }, [itemCoverProfile]);

  // Scroll to the selected item when `selectedItem` changes
  useEffect(() => {
    if (!api || selectedItem === null) return;

    const index = userAllCoverProfiles.findIndex(
      (profile: any) => profile.picture === selectedItem.picture
    );

    if (index !== -1 && !isScrolling.current) {
      api.scrollTo(index); // Scroll to the index
    }
  }, [api, selectedItem, userAllCoverProfiles]);

  // Detect index changes when scrolling or snapping
  useEffect(() => {
    if (!api) return;

    const handleSelect = () => {
      isScrolling.current = true;
      const currentIndex = api.selectedScrollSnap();
      const newSelectedItem = userAllCoverProfiles[currentIndex];

      if (
        newSelectedItem &&
        newSelectedItem.picture !== selectedItem?.picture
      ) {
        setSelectedItem(newSelectedItem);
      }
      isScrolling.current = false;
    };

    // Listen to Embla's `select` event for slide changes
    api.on("select", handleSelect);

    return () => {
      api.off("select", handleSelect); // Cleanup
    };
  }, [api, userAllCoverProfiles, selectedItem]);

  return (
    <Dialog open={isDialogOpen}>
      <DialogContent
        className="max-w-[715px] w-[400px] md:w-[600px] lg:w-[715px]"
        isShowBtnXClose={false}
      >
        <DialogHeader className="">
          <DialogTitle>Edit Profile Cover</DialogTitle>
          <DialogDescription>
            Make changes to your profile here. Click save when you're done.
          </DialogDescription>
          <div className="absolute right-14 top-1 flex items-center space-x-2">
            {/* Trash2 Button - Delete Cover */}
            <div className="flex items-center justify-center z-40 cursor-pointer rounded-full bg-gray-200 dark:bg-accent dark:hover:bg-slate-900 w-[28px] h-[28px] p-1">
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Trash2 />
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>
                      Are you sure you want to delete this?
                    </AlertDialogTitle>
                    <AlertDialogDescription>
                      This action cannot be undone. Click continue to delete
                      this cover picture.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={() => {
                        onValueDelete(selectedItem);
                      }}
                      className="text-white bg-blue-600 hover:bg-blue-500"
                    >
                      Continue
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </div>

          {/* Close Button */}
          <div
            className="absolute flex items-center justify-center right-4 top-1 z-40 cursor-pointer rounded-full bg-gray-200 dark:bg-accent w-[28px] h-[28px]"
            onClick={() => setIsDialogOpen(false)}
          >
            <X />
          </div>
        </DialogHeader>
        <Carousel setApi={setApi} className="z-50">
          <CarouselContent className="w-full ml-0">
            {Array.isArray(userAllCoverProfiles) &&
              userAllCoverProfiles.map((item: any, index: number) => (
                <CarouselItem
                  key={index}
                  className="relative pb-[75px] pl-0 flex justify-center items-center"
                >
                  <ImageUrl
                    src={item?.picture}
                    className="w-full object-cover rounded-t-none sm:rounded-t-lg"
                    alt="menu"
                  />
                </CarouselItem>
              ))}
          </CarouselContent>
        </Carousel>
      </DialogContent>
    </Dialog>
  );
}
